import { useState, useCallback } from 'react';
import { Modal, message } from 'antd';
import { d211Api, FD110, ApprovalHistoryEvent } from '../../../services/d211Api';

interface UseD211ApprovalProps {
  record: FD110 | null;
  isEditing: boolean;
  loadRecord: (id: string) => Promise<FD110 | null>;
}

export function useD211Approval({ record, isEditing, loadRecord }: UseD211ApprovalProps) {
  const [historyVisible, setHistoryVisible] = useState(false);
  const [history, setHistory] = useState<ApprovalHistoryEvent[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const handleSubmit = useCallback(() => {
    if (!record || isEditing) return;
    const id = record.fd110_01;
    Modal.confirm({
      title: '確認送審',
      content: `是否將採購單 ${id} 送審？`,
      okText: '確認',
      cancelText: '取消',
      onOk: async () => {
        try {
          await d211Api.submitForApproval(id);
          message.success('送審成功');
          await loadRecord(id);
        } catch (err: unknown) {
          const axiosErr = err as { response?: { data?: { error?: string } } };
          message.error(axiosErr?.response?.data?.error || '送審失敗');
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleApprove = useCallback(() => {
    if (!record || isEditing) return;
    const id = record.fd110_01;
    Modal.confirm({
      title: '確認核准',
      content: `是否核准採購單 ${id}？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          await d211Api.approve(id);
          message.success('核准成功');
          await loadRecord(id);
        } catch (err: unknown) {
          const axiosErr = err as { response?: { data?: { error?: string } } };
          message.error(axiosErr?.response?.data?.error || '核准失敗');
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleReject = useCallback(() => {
    if (!record || isEditing) return;
    const id = record.fd110_01;
    Modal.confirm({
      title: '確認退回',
      content: `是否退回採購單 ${id}？`,
      okText: '退回',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await d211Api.reject(id);
          message.success('退回成功');
          await loadRecord(id);
        } catch (err: unknown) {
          const axiosErr = err as { response?: { data?: { error?: string } } };
          message.error(axiosErr?.response?.data?.error || '退回失敗');
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleViewHistory = useCallback(async () => {
    if (!record) return;
    setHistoryLoading(true);
    setHistoryVisible(true);
    try {
      const events = await d211Api.getApprovalHistory(record.fd110_01);
      setHistory(events);
    } catch {
      message.error('讀取審核歷史失敗');
    } finally {
      setHistoryLoading(false);
    }
  }, [record]);

  const handleWriteoff = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.fd110_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await d211Api.writeoff(record.fd110_01);
          message.success('沖銷成功');
          await loadRecord(record.fd110_01);
        } catch (err: unknown) {
          const axiosErr = err as { response?: { data?: { error?: string } } };
          message.error(axiosErr?.response?.data?.error || '沖銷失敗');
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const closeHistory = useCallback(() => {
    setHistoryVisible(false);
  }, []);

  return {
    handleSubmit,
    handleApprove,
    handleReject,
    handleWriteoff,
    handleViewHistory,
    closeHistory,
    historyVisible,
    history,
    historyLoading,
  };
}
